import React, { useState, useEffect, useRef } from "react";

// components
import Navbar from "components/Navbars/AuthNavbar.js";
import Footer from "components/Footers/Footer.js";

// context
import { useLanguage } from "contexts/LanguageContext.js";

export default function Landing() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    user_type: "",
  });
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formStartTracked, setFormStartTracked] = useState(false);
  const [scrollToContactTracked, setScrollToContactTracked] = useState(false);
  const contactSectionRef = useRef(null);

  // Quick signup state
  const [quickSignupEmail, setQuickSignupEmail] = useState("");
  const [isQuickSignupSubmitting, setIsQuickSignupSubmitting] = useState(false);
  const [quickSignupStatus, setQuickSignupStatus] = useState({ type: "", message: "" });

  // Track scroll to contact section
  useEffect(() => {
    const handleScroll = () => {
      if (scrollToContactTracked || !contactSectionRef.current) return;

      const rect = contactSectionRef.current.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.top <= window.innerHeight;

      if (isVisible) {
        if (window.gtag) {
          window.gtag('event', 'scroll_to_contact', {
            'event_category': 'engagement',
            'event_label': 'contact_section_visible'
          });
        }
        setScrollToContactTracked(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrollToContactTracked]);

  // Track CTA click
  const handleCtaClick = () => {
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        'event_category': 'engagement',
        'event_label': 'hero_join_beta_button'
      });
    }
  };

  // Track form start (first input focus)
  const handleFormStart = () => {
    if (!formStartTracked) {
      if (window.gtag) {
        window.gtag('event', 'form_start', {
          'event_category': 'engagement',
          'event_label': 'contact_form_started'
        });
      }
      setFormStartTracked(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8080"}/contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setFormStatus({
          type: "success",
          message: data.message || "¡Gracias! Te contactaremos pronto.",
        });
        setFormData({ name: "", email: "", message: "", user_type: "" });
        setShowSuccess(true);

        // Track conversion event in Google Analytics
        if (window.gtag) {
          window.gtag('event', 'contact_form_submit', {
            'event_category': 'engagement',
            'event_label': 'landing_contact_form',
            'form_location': 'landing_page',
            'value': 1
          });
        }

        // Auto-hide success message after 10 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 10000);
      } else {
        setFormStatus({
          type: "error",
          message:
            data.message || "Error al enviar el mensaje. Intenta nuevamente.",
        });
      }
    } catch (error) {
      setFormStatus({
        type: "error",
        message: "Error de conexión. Por favor intenta más tarde.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  // Handle quick signup from hero section
  const handleQuickSignup = async (e) => {
    e.preventDefault();

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!quickSignupEmail || !emailRegex.test(quickSignupEmail)) {
      setQuickSignupStatus({
        type: "error",
        message: "Por favor ingresa un email válido"
      });
      return;
    }

    setIsQuickSignupSubmitting(true);
    setQuickSignupStatus({ type: "", message: "" });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8080"}/contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            type: "quick_signup",
            email: quickSignupEmail,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setQuickSignupStatus({
          type: "success",
          message: data.message || "¡Gracias! Te contactaremos pronto.",
        });
        setQuickSignupEmail("");

        // Track conversion event in Google Analytics
        if (window.gtag) {
          window.gtag('event', 'quick_signup_submit', {
            'event_category': 'conversion',
            'event_label': 'hero_quick_signup',
            'form_location': 'hero_section',
            'value': 1
          });
        }

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setQuickSignupStatus({ type: "", message: "" });
        }, 5000);
      } else {
        setQuickSignupStatus({
          type: "error",
          message: data.message || "Error al registrarte. Intenta nuevamente.",
        });
      }
    } catch (error) {
      setQuickSignupStatus({
        type: "error",
        message: "Error de conexión. Por favor intenta más tarde.",
      });
    } finally {
      setIsQuickSignupSubmitting(false);
    }
  };

  return (
    <>
      <Navbar transparent />
      <main>
        {/* Hero Section with Video */}
        <div className="relative pt-16 pb-16 flex content-center items-center justify-center min-h-screen-75">
          <div
            className="absolute top-0 w-full h-full bg-center bg-cover"
            style={{
              background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
            }}
          >
            <span
              id="blackOverlay"
              className="w-full h-full absolute opacity-30 bg-black"
            ></span>
          </div>
          <div className="container relative mx-auto">
            <div className="items-center flex flex-wrap">
              <div className="w-full lg:w-6/12 px-4 ml-auto mr-auto text-center">
                <div className="lg:pr-12">
                  <h1 className="text-white font-semibold text-5xl mb-4">
                    {t('hero.title')}
                  </h1>
                  <p className="mt-4 text-xl text-white leading-relaxed" dangerouslySetInnerHTML={{ __html: t('hero.description') }} />

                  {/* CTA Button */}
                  <div className="mt-8">
                    <button
                      onClick={() => {
                        handleCtaClick();
                        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      style={{
                        background: 'linear-gradient(to right, #34D399, #22D3EE)',
                        color: '#1a202c',
                        padding: '1rem 2rem',
                        fontSize: '1.125rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                        borderRadius: '0.5rem',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                        transition: 'all 0.3s ease',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #10B981, #06B6D4)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 30px 60px -12px rgba(0, 0, 0, 0.6)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #34D399, #22D3EE)';
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 25px 50px -12px rgba(0, 0, 0, 0.5)';
                      }}
                    >
                      <span style={{ marginRight: '0.5rem' }}>🚀</span>
                      {t('hero.ctaButton')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className="top-auto bottom-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden h-70-px"
            style={{ transform: "translateZ(0)" }}
          >
            <svg
              className="absolute bottom-0 overflow-hidden w-full h-full block"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
              style={{ display: 'block' }}
            >
              <polygon
                className="text-blueGray-200 fill-current"
                points="2560 0 2560 100 0 100"
              ></polygon>
            </svg>
          </div>
        </div>

        {/* Features Section - AI Agents */}
        <section id="agents-section" className="pb-10 bg-blueGray-200 -mt-px">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center text-center mb-12 pt-12">
              <div className="w-full lg:w-8/12 px-4">
                <h2 className="text-4xl font-semibold text-blueGray-700">
                  {t('agents.sectionTitle')}
                </h2>
                <p className="text-lg leading-relaxed mt-4 text-blueGray-600" dangerouslySetInnerHTML={{ __html: t('agents.sectionSubtitle') }} />
              </div>
            </div>

            <div className="flex flex-wrap justify-center">
              {/* MAITE - Ingeniero */}
              <div className="w-full md:w-6/12 lg:w-1/5 px-4 text-center">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300">
                  <div className="px-4 py-5 flex-auto">
                    <div className="text-white p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-lightBlue-500">
                      <i className="fas fa-robot text-2xl"></i>
                    </div>
                    <h6 className="text-xl font-semibold">
                      {t('agents.maite.title')}
                    </h6>
                    <p className="mt-2 mb-4 text-blueGray-500">
                      {t('agents.maite.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* TATON - Product Owner */}
              <div className="w-full md:w-6/12 lg:w-1/5 px-4 text-center">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300">
                  <div className="px-4 py-5 flex-auto">
                    <div className="text-white p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-purple-500">
                      <i className="fas fa-clipboard-list text-2xl"></i>
                    </div>
                    <h6 className="text-xl font-semibold">
                      {t('agents.taton.title')}
                    </h6>
                    <p className="mt-2 mb-4 text-blueGray-500">
                      {t('agents.taton.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* PUA - QA */}
              <div className="w-full md:w-6/12 lg:w-1/5 px-4 text-center">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300">
                  <div className="px-4 py-5 flex-auto">
                    <div className="text-white p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-emerald-500">
                      <i className="fas fa-check-circle text-2xl"></i>
                    </div>
                    <h6 className="text-xl font-semibold">{t('agents.pua.title')}</h6>
                    <p className="mt-2 mb-4 text-blueGray-500">
                      {t('agents.pua.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* MELU - Marketing */}
              <div className="w-full md:w-6/12 lg:w-1/5 px-4 text-center">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300">
                  <div className="px-4 py-5 flex-auto">
                    <div className="text-white p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-red-500">
                      <i className="fas fa-chart-line text-2xl"></i>
                    </div>
                    <h6 className="text-xl font-semibold">
                      {t('agents.melu.title')}
                    </h6>
                    <p className="mt-2 mb-4 text-blueGray-500">
                      {t('agents.melu.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* CAI - Designer */}
              <div className="w-full md:w-6/12 lg:w-1/5 px-4 text-center">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full mb-8 shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300">
                  <div className="px-4 py-5 flex-auto">
                    <div className="text-white p-3 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-pink-500">
                      <i className="fas fa-palette text-2xl"></i>
                    </div>
                    <h6 className="text-xl font-semibold">
                      {t('agents.cai.title')}
                    </h6>
                    <p className="mt-2 mb-4 text-blueGray-500">
                      {t('agents.cai.description')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="relative py-10 bg-white">
          <div className="container mx-auto px-4">
            <div className="items-center flex flex-wrap mb-12">
              <div className="w-full md:w-5/12 ml-auto mr-auto px-4">
                <h3 className="text-3xl mb-2 font-semibold leading-normal">
                  {t('howItWorks.title')}
                </h3>
                <p className="text-lg font-light leading-relaxed mt-4 mb-4 text-blueGray-600">
                  {t('howItWorks.description')}
                </p>
                <ul className="list-none mt-6">
                  <li className="py-2">
                    <div className="flex items-start">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-lightBlue-600 bg-lightBlue-200 mr-3">
                          <i className="fas fa-sync-alt"></i>
                        </span>
                      </div>
                      <div>
                        <h4 className="text-blueGray-700 font-semibold">
                          {t('howItWorks.automaticExecution.title')}
                        </h4>
                        <p className="text-blueGray-500 text-sm">
                          {t('howItWorks.automaticExecution.description')}
                        </p>
                      </div>
                    </div>
                  </li>
                  <li className="py-2">
                    <div className="flex items-start">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-purple-600 bg-purple-200 mr-3">
                          <i className="fab fa-github"></i>
                        </span>
                      </div>
                      <div>
                        <h4 className="text-blueGray-700 font-semibold">
                          {t('howItWorks.githubIntegration.title')}
                        </h4>
                        <p className="text-blueGray-500 text-sm">
                          {t('howItWorks.githubIntegration.description')}
                        </p>
                      </div>
                    </div>
                  </li>
                  <li className="py-2">
                    <div className="flex items-start">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-emerald-600 bg-emerald-200 mr-3">
                          <i className="fas fa-comments"></i>
                        </span>
                      </div>
                      <div>
                        <h4 className="text-blueGray-700 font-semibold">
                          {t('howItWorks.collaboration.title')}
                        </h4>
                        <p className="text-blueGray-500 text-sm">
                          {t('howItWorks.collaboration.description')}
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="w-full md:w-5/12 ml-auto mr-auto px-4 mt-8 md:mt-0">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6">
                  <img
                    alt="BEDCEO Dashboard"
                    src="/landing/dashboard.jpg"
                    className="w-full rounded-lg shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BE THE CEO Section */}
        <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #3B82F6 100%)" }}>
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -ml-48 -mb-48"></div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="flex flex-wrap justify-center text-center mb-16">
              <div className="w-full lg:w-10/12 px-4 pb-6">
                <h2 className="text-5xl font-bold text-white mb-4">
                  {t('beTheCEO.title')}
                </h2>
                <p className="text-2xl text-white opacity-90 mb-6">
                  {t('beTheCEO.subtitle')}
                </p>
                <p className="text-xl text-white leading-relaxed max-w-3xl mx-auto" dangerouslySetInnerHTML={{ __html: t('beTheCEO.description') }} />
              </div>
            </div>

            <div className="flex flex-wrap justify-center -mx-4">
              {/* Strategy */}
              <div className="w-full md:w-6/12 lg:w-3/12 px-4 mb-8">
                <div className="bg-white rounded-lg shadow-xl p-8 h-full transform hover:scale-105 transition-all duration-300">
                  <div className="text-purple-600 mb-4">
                    <i className="fas fa-chess-king text-5xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-blueGray-700 mb-3">
                    {t('beTheCEO.responsibilities.strategy.title')}
                  </h5>
                  <p className="text-blueGray-500 leading-relaxed">
                    {t('beTheCEO.responsibilities.strategy.description')}
                  </p>
                </div>
              </div>

              {/* Prioritize */}
              <div className="w-full md:w-6/12 lg:w-3/12 px-4 mb-8">
                <div className="bg-white rounded-lg shadow-xl p-8 h-full transform hover:scale-105 transition-all duration-300">
                  <div className="text-lightBlue-600 mb-4">
                    <i className="fas fa-sort-amount-down text-5xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-blueGray-700 mb-3">
                    {t('beTheCEO.responsibilities.prioritize.title')}
                  </h5>
                  <p className="text-blueGray-500 leading-relaxed">
                    {t('beTheCEO.responsibilities.prioritize.description')}
                  </p>
                </div>
              </div>

              {/* Identify */}
              <div className="w-full md:w-6/12 lg:w-3/12 px-4 mb-8">
                <div className="bg-white rounded-lg shadow-xl p-8 h-full transform hover:scale-105 transition-all duration-300">
                  <div className="text-emerald-600 mb-4">
                    <i className="fas fa-lightbulb text-5xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-blueGray-700 mb-3">
                    {t('beTheCEO.responsibilities.identify.title')}
                  </h5>
                  <p className="text-blueGray-500 leading-relaxed">
                    {t('beTheCEO.responsibilities.identify.description')}
                  </p>
                </div>
              </div>

              {/* Definitions */}
              <div className="w-full md:w-6/12 lg:w-3/12 px-4 mb-8">
                <div className="bg-white rounded-lg shadow-xl p-8 h-full transform hover:scale-105 transition-all duration-300">
                  <div className="text-red-600 mb-4">
                    <i className="fas fa-file-alt text-5xl"></i>
                  </div>
                  <h5 className="text-xl font-bold text-blueGray-700 mb-3">
                    {t('beTheCEO.responsibilities.definitions.title')}
                  </h5>
                  <p className="text-blueGray-500 leading-relaxed">
                    {t('beTheCEO.responsibilities.definitions.description')}
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-3xl font-bold text-white">
                {t('beTheCEO.cta')}
              </p>
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-10 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center text-center mb-16">
              <div className="w-full lg:w-8/12 px-4">
                <h2 className="text-4xl font-semibold text-blueGray-700">
                  {t('useCases.title')}
                </h2>
                <p className="text-lg leading-relaxed my-4 text-blueGray-600">
                  {t('useCases.description')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap">
              {/* Developers */}
              <div className="w-full md:w-4/12 px-4 mb-8">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-lightBlue-500">
                  <div className="px-6 py-8 flex-auto">
                    <div className="text-white p-4 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-lightBlue-500">
                      <i className={`fas ${t('useCases.developers.icon')} text-2xl`}></i>
                    </div>
                    <h6 className="text-2xl font-bold mb-4 text-blueGray-700">
                      {t('useCases.developers.title')}
                    </h6>
                    <p className="mb-4 text-blueGray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('useCases.developers.description') }} />
                    <div className="my-6 pt-6 border-t border-blueGray-200">
                      <p className="text-sm text-lightBlue-600 italic">
                        <i className="fas fa-check-circle mr-2"></i>
                        {t('useCases.developers.benefit')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Entrepreneurs */}
              <div className="w-full md:w-4/12 px-4 mb-8">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-purple-500">
                  <div className="px-6 py-8 flex-auto">
                    <div className="text-white p-4 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-purple-500">
                      <i className={`fas ${t('useCases.entrepreneurs.icon')} text-2xl`}></i>
                    </div>
                    <h6 className="text-2xl font-bold mb-4 text-blueGray-700">
                      {t('useCases.entrepreneurs.title')}
                    </h6>
                    <p className="mb-4 text-blueGray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('useCases.entrepreneurs.description') }} />
                    <div className="my-6 pt-6 border-t border-blueGray-200">
                      <p className="text-sm text-lightBlue-600 italic">
                        <i className="fas fa-check-circle mr-2"></i>
                        {t('useCases.entrepreneurs.benefit')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Startups */}
              <div className="w-full md:w-4/12 px-4 mb-8">
                <div className="relative flex flex-col min-w-0 break-words bg-white w-full shadow-lg rounded-lg hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-emerald-500">
                  <div className="px-6 py-8 flex-auto">
                    <div className="text-white p-4 text-center inline-flex items-center justify-center w-16 h-16 mb-5 shadow-lg rounded-full bg-emerald-500">
                      <i className={`fas ${t('useCases.startups.icon')} text-2xl`}></i>
                    </div>
                    <h6 className="text-2xl font-bold mb-4 text-blueGray-700">
                      {t('useCases.startups.title')}
                    </h6>
                    <p className="mb-4 text-blueGray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('useCases.startups.description') }} />
                    <div className="my-6 pt-6 border-t border-blueGray-200">
                      <p className="text-sm text-lightBlue-600 italic">
                        <i className="fas fa-check-circle mr-2"></i>
                        {t('useCases.startups.benefit')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Demo Video Section */}
        <section className="py-10 bg-blueGray-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center text-center mb-12">
              <div className="w-full lg:w-8/12 px-4">
                <h2 className="text-4xl font-semibold text-blueGray-700">
                  {t('demo.title')}
                </h2>
                <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-600">
                  {t('demo.description')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap justify-center">
              <div className="w-full lg:w-10/12 px-4">
                {/* Demo Video */}
                <div className="relative bg-blueGray-800 rounded-lg shadow-2xl overflow-hidden" style={{ paddingBottom: "56.25%" }}>
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://player.vimeo.com/video/1140512689?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                    title="BEDCEO Demo"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="pb-20 relative block bg-blueGray-800">
          <div
            className="bottom-auto top-0 left-0 right-0 w-full absolute pointer-events-none overflow-hidden -mt-20 h-20"
            style={{ transform: "translateZ(0)" }}
          >
            <svg
              className="absolute bottom-0 overflow-hidden w-full h-full block"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
              version="1.1"
              viewBox="0 0 2560 100"
              x="0"
              y="0"
              style={{ display: 'block' }}
            >
              <polygon
                className="text-blueGray-800 fill-current"
                points="2560 0 2560 100 0 100"
              ></polygon>
            </svg>
          </div>

          <div className="container mx-auto px-4 lg:pt-24 lg:pb-64">
            <div className="flex flex-wrap text-center justify-center mb-12">
              <div className="w-full lg:w-8/12 px-4">
                <h2 className="text-4xl font-semibold text-white">
                  {t('benefits.title')}
                </h2>
                <p className="text-lg leading-relaxed mt-4 mb-4 text-blueGray-400">
                  {t('benefits.description')}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap mt-12 justify-center">
              <div className="w-full lg:w-3/12 px-4 text-center">
                <div className="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                  <i className="fas fa-rocket text-xl"></i>
                </div>
                <h6 className="text-xl mt-5 font-semibold text-white">
                  {t('benefits.continuousDevelopment.title')}
                </h6>
                <p className="mt-2 mb-4 text-blueGray-400">
                  {t('benefits.continuousDevelopment.description')}
                </p>
              </div>
              <div className="w-full lg:w-3/12 px-4 text-center">
                <div className="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                  <i className="fas fa-dollar-sign text-xl"></i>
                </div>
                <h5 className="text-xl mt-5 font-semibold text-white">
                  {t('benefits.costReduction.title')}
                </h5>
                <p className="mt-2 mb-4 text-blueGray-400">
                  {t('benefits.costReduction.description')}
                </p>
              </div>
              <div className="w-full lg:w-3/12 px-4 text-center">
                <div className="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                  <i className="fas fa-users text-xl"></i>
                </div>
                <h5 className="text-xl mt-5 font-semibold text-white">
                  {t('benefits.specializedTeam.title')}
                </h5>
                <p className="mt-2 mb-4 text-blueGray-400">
                  {t('benefits.specializedTeam.description')}
                </p>
              </div>
              <div className="w-full lg:w-3/12 px-4 text-center">
                <div className="text-blueGray-800 p-3 w-12 h-12 shadow-lg rounded-full bg-white inline-flex items-center justify-center">
                  <i className="fas fa-brain text-xl"></i>
                </div>
                <h5 className="text-xl mt-5 font-semibold text-white">
                  {t('benefits.smartDecisions.title')}
                </h5>
                <p className="mt-2 mb-4 text-blueGray-400">
                  {t('benefits.smartDecisions.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section
          id="contact"
          ref={contactSectionRef}
          className="relative block py-30 lg:pt-0 bg-blueGray-800 -mt-px"
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center lg:-mt-64 -mt-20">
              <div className="w-full lg:w-6/12 px-4">
                <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-200">
                  <div className="flex-auto p-5 lg:p-10">
                    <h4 className="text-2xl font-semibold text-blueGray-700">
                      {t('contact.title')}
                    </h4>
                    <p className="leading-relaxed mt-1 mb-4 text-blueGray-500">
                      {t('contact.description')}
                    </p>

                    {formStatus.type === "success" && showSuccess && (
                      <div className="mb-6 p-6 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100 border-2 border-emerald-400 shadow-xl animate-fadeIn">
                        <div className="text-center">
                          {/* Success Icon with animation */}
                          <div className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-full bg-emerald-500 shadow-lg animate-bounce-slow">
                            <i className="fas fa-check text-4xl text-white"></i>
                          </div>

                          {/* Main success message */}
                          <h3 className="text-2xl font-bold text-emerald-800 mb-2">
                            {t('contact.successTitle')}
                          </h3>

                          {/* Timeline and email confirmation */}
                          <p className="text-emerald-700 text-lg mb-1" dangerouslySetInnerHTML={{ __html: t('contact.successMessage') }} />
                          <p className="text-emerald-600 text-sm mb-6">
                            {t('contact.emailConfirmation')}
                          </p>

                          {/* Call to action button */}
                          <div className="flex flex-wrap justify-center gap-3 mt-6">
                            <button
                              onClick={() => {
                                document.getElementById('agents-section')?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              className="inline-flex items-center px-5 py-3 bg-gradient-to-r from-lightBlue-500 to-purple-500 hover:from-lightBlue-600 hover:to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-sm"
                            >
                              <i className="fas fa-robot mr-2"></i>
                              {t('contact.knowAgentsButton')}
                            </button>
                          </div>

                          {/* Close button */}
                          <button
                            onClick={() => {
                              setShowSuccess(false);
                            }}
                            className="mt-4 text-emerald-600 hover:text-emerald-800 text-sm font-medium underline"
                            aria-label={t('contact.closeButton')}
                          >
                            {t('contact.closeButton')}
                          </button>
                        </div>
                      </div>
                    )}

                    {formStatus.type === "error" && formStatus.message && (
                      <div className="mb-4 p-4 rounded bg-red-100 text-red-700 border border-red-300">
                        <div className="flex items-center">
                          <i className="fas fa-exclamation-circle mr-2"></i>
                          {formStatus.message}
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="relative w-full mb-3 mt-8">
                        <label
                          className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                          htmlFor="name"
                        >
                          {t('contact.form.nameLabel')}
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          onFocus={handleFormStart}
                          className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                          placeholder={t('contact.form.namePlaceholder')}
                          autoComplete="name"
                          required
                        />
                      </div>

                      <div className="relative w-full mb-3">
                        <label
                          className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                          htmlFor="email"
                        >
                          {t('contact.form.emailLabel')}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onFocus={handleFormStart}
                          className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                          placeholder={t('contact.form.emailPlaceholder')}
                          autoComplete="email"
                          required
                        />
                      </div>

                      {/* User Type Selector */}
                      <div className="relative w-full mb-3">
                        <label className="block uppercase text-blueGray-600 text-xs font-bold mb-2">
                          {t('contact.form.userTypeLabel')} <span className="text-blueGray-400 normal-case text-xs">({t('contact.form.optional')})</span>
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Solo Developer Card */}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, user_type: prev.user_type === 'solo_developer' ? '' : 'solo_developer' }));
                              if (window.gtag) {
                                window.gtag('event', 'user_type_selected', {
                                  'event_category': 'engagement',
                                  'event_label': 'solo_developer',
                                  'form_location': 'contact_form'
                                });
                              }
                            }}
                            className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                              formData.user_type === 'solo_developer'
                                ? 'border-lightBlue-500 bg-lightBlue-50 shadow-md'
                                : 'border-blueGray-200 bg-white hover:border-lightBlue-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-center mb-2">
                              <span className="text-2xl mr-2">👨‍💻</span>
                              <h6 className="text-sm font-bold text-blueGray-700">
                                {t('contact.form.userTypes.soloDeveloper.title')}
                              </h6>
                            </div>
                            <p className="text-xs text-blueGray-500">
                              {t('contact.form.userTypes.soloDeveloper.description')}
                            </p>
                          </button>

                          {/* Entrepreneur Card */}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, user_type: prev.user_type === 'entrepreneur' ? '' : 'entrepreneur' }));
                              if (window.gtag) {
                                window.gtag('event', 'user_type_selected', {
                                  'event_category': 'engagement',
                                  'event_label': 'entrepreneur',
                                  'form_location': 'contact_form'
                                });
                              }
                            }}
                            className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                              formData.user_type === 'entrepreneur'
                                ? 'border-purple-500 bg-purple-50 shadow-md'
                                : 'border-blueGray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-center mb-2">
                              <span className="text-2xl mr-2">🚀</span>
                              <h6 className="text-sm font-bold text-blueGray-700">
                                {t('contact.form.userTypes.entrepreneur.title')}
                              </h6>
                            </div>
                            <p className="text-xs text-blueGray-500">
                              {t('contact.form.userTypes.entrepreneur.description')}
                            </p>
                          </button>

                          {/* Startup Team Card */}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, user_type: prev.user_type === 'startup_team' ? '' : 'startup_team' }));
                              if (window.gtag) {
                                window.gtag('event', 'user_type_selected', {
                                  'event_category': 'engagement',
                                  'event_label': 'startup_team',
                                  'form_location': 'contact_form'
                                });
                              }
                            }}
                            className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                              formData.user_type === 'startup_team'
                                ? 'border-emerald-500 bg-emerald-50 shadow-md'
                                : 'border-blueGray-200 bg-white hover:border-emerald-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-center mb-2">
                              <span className="text-2xl mr-2">🏢</span>
                              <h6 className="text-sm font-bold text-blueGray-700">
                                {t('contact.form.userTypes.startupTeam.title')}
                              </h6>
                            </div>
                            <p className="text-xs text-blueGray-500">
                              {t('contact.form.userTypes.startupTeam.description')}
                            </p>
                          </button>
                        </div>
                      </div>

                      <div className="relative w-full mb-3">
                        <label
                          className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                          htmlFor="message"
                        >
                          {t('contact.form.messageLabel')}
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          rows="4"
                          cols="80"
                          value={formData.message}
                          onChange={handleInputChange}
                          onFocus={handleFormStart}
                          className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full"
                          placeholder={t('contact.form.messagePlaceholder')}
                          autoComplete="off"
                          required
                        />
                      </div>
                      <div className="text-center mt-6">
                        <button
                          className={`bg-blueGray-800 text-white active:bg-blueGray-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 ${
                            isSubmitting
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i>
                              {t('contact.form.submittingButton')}
                            </>
                          ) : (
                            t('contact.form.submitButton')
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
